import { z } from 'zod';
/**
 * WebSocket protocol between the Elena server and every client.
 * Every message is JSON with a `type` discriminator. Clients authenticate with
 * `Authorization: Bearer <ELENA_TOKEN>` (or `?token=` for browsers) before the first message.
 */
/**
 * caller = Elena's phone: a page (#/caller, headless Chrome on the VPS or any browser) that carries the
 * voice of ADMIN LINE calls Elena makes for you. It answers the player's WebRTC offer, plays what Elena
 * says, and sends back what the player said.
 */
export const deviceKinds = ['agent', 'web', 'mobile', 'caller'];
const id = z.string().min(1).max(64);
// ---------- client → server ----------
export const helloMessage = z.object({
    type: z.literal('hello'),
    deviceId: id,
    kind: z.enum(deviceKinds),
    name: z.string().min(1).max(60),
    /** Actions this device can execute (only meaningful for the desktop agent). */
    capabilities: z.array(z.string()).max(100).default([]),
    /** The agent's TVs by room name (ELENA_TVS), so Elena can pick one. */
    tvs: z.array(z.string().min(1).max(40)).max(10).optional(),
    version: z.string().max(20).optional(),
});
export const chatMessage = z.object({
    type: z.literal('chat'),
    text: z.string().min(1).max(2000),
    /** Set by the client so the server can apply origin-based permissions. */
    origin: z.enum(['pc', 'phone', 'web']),
    /** True when the text came from speech recognition; Elena answers shorter. */
    spoken: z.boolean().default(false),
    /** True while the client is in hands-free conversation mode. */
    conversation: z.boolean().default(false),
    /**
     * The recognizer's other guesses for the same speech (best first, text excluded), so Elena
     * can pick the one that makes sense when a word was misheard.
     */
    alternatives: z.array(z.string().min(1).max(2000)).max(4).optional(),
});
export const actionResultMessage = z.object({
    type: z.literal('action_result'),
    requestId: id,
    ok: z.boolean(),
    message: z.string().max(500),
});
export const confirmReplyMessage = z.object({
    type: z.literal('confirm_reply'),
    confirmId: id,
    approve: z.boolean(),
});
export const pingMessage = z.object({ type: z.literal('ping') });
/** SDP is plain ASCII lines (same rule as the ADMIN LINE in the admin panel). */
const sdp = z
    .string()
    .max(16_000)
    .regex(/^v=0\r?\n[\x20-\x7e\r\n]*$/);
const callId = z.string().min(8).max(64);
/** Elena's phone answered the player's offer: this is its SDP answer. */
export const callAnswerMessage = z.object({ type: z.literal('call_answer'), callId, sdp });
/** What the player said (one finished sentence), with the recognizer's other guesses. */
export const callHeardMessage = z.object({
    type: z.literal('call_heard'),
    callId,
    text: z.string().min(1).max(1000),
    alternatives: z.array(z.string().min(1).max(1000)).max(3).optional(),
    /** The player talked over Elena, so she stopped. */
    interrupted: z.boolean().optional(),
});
/** Voice connected / failed / the page dropped the call; speaking = Elena's audio started or ended. */
export const callStateMessage = z.object({
    type: z.literal('call_state'),
    callId,
    state: z.enum(['connected', 'failed', 'ended', 'speaking', 'quiet']),
    detail: z.string().max(200).optional(),
});
/**
 * How the call sounds on Elena's phone, every few seconds and on errors: the audio clock, WebRTC
 * bytes and levels, what she heard. Logged on the server, because nobody sees the headless page.
 */
export const callReportMessage = z.object({
    type: z.literal('call_report'),
    callId,
    report: z
        .record(z.string().max(40), z.union([z.string().max(300), z.number(), z.boolean(), z.null()]))
        .refine((r) => Object.keys(r).length <= 40),
});
export const clientMessage = z.discriminatedUnion('type', [
    helloMessage,
    chatMessage,
    actionResultMessage,
    confirmReplyMessage,
    pingMessage,
    callAnswerMessage,
    callHeardMessage,
    callStateMessage,
    callReportMessage,
]);
export function parseClientMessage(raw) {
    return clientMessage.parse(JSON.parse(raw));
}
//# sourceMappingURL=protocol.js.map